import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { fmtNum } from "./dgiiUtils";

// Parser del Formato de Envío 606 (Compras de Bienes y Servicios) de la DGII.
// Archivo delimitado por "|". La primera línea es el encabezado: 606|RNC|Periodo|Cantidad.
// Columnas de detalle (1-indexado):
// 10 Total Monto Facturado, 11 ITBIS Facturado, 12 ITBIS Retenido,
// 13 ITBIS sujeto a Proporcionalidad (Art. 349), 14 ITBIS llevado al Costo,
// 15 ITBIS por Adelantar, 16 ITBIS percibido en compras,
// 18 Monto Retención Renta, 19 ISR percibido en compras.
const parse606 = (text, filename) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  const t = {
    total_facturado: 0, itbis_facturado: 0, itbis_retenido: 0, itbis_adelantar: 0,
    itbis_costo: 0, retencion_renta: 0, itbis_proporcionalidad: 0,
    itbis_percibido: 0, isr_percibido: 0,
  };
  let registros = 0;
  let periodo = "";
  let rnc = "";
  let datos = [];

  for (const line of lines) {
    const c = line.split("|");
    const first = (c[0] || "").trim();
    if (first === "606") {
      rnc = (c[1] || "").trim();
      periodo = (c[2] || "").trim();
      continue;
    }
    if (c.length < 11) continue;
    const n = (i) => {
      const v = parseFloat((c[i] || "").trim().replace(/,/g, ""));
      return isNaN(v) ? 0 : v;
    };
    registros++;
    // Guardar cada línea en datos
    // "03103319988|2|09|E310000012013||20260612|20260612|0|51440.68|51440.68|9259.32|0|0|0|9259.32|0|00|0|0|0|0|0|01


    datos.push({
      rnc: c[0]?.trim(),
      tipo_id: c[1]?.trim(),
      bien_o_servicio: c[2]?.trim(),
      ncf: c[3]?.trim(),
      ncf_modificado: c[4]?.trim(),
      fecha_comprobante: c[5]?.trim(),
      fecha_pago: c[6]?.trim(),
      monto_facturado_servicio: n(7),
      monto_facturado_bien: n(8),
      total_facturado: n(9),
      itbis_facturado: n(10),
      itbis_retenido: n(11),
      itbis_sujeto_proporcionalidad: n(12),
      itbis_costo: n(13),
      itbis_adelantar: n(14),
      itbis_percibido_compra: n(15),
      tipo_retencion_isr: n(16),
      monto_retencion_renta: n(17),
      isr_precibido: n(18),
      impuesto_selectivo_consumo: n(19),
      otros_impuesto: n(20),
      monto_propinalegal: n(21),
      forma_pago: n(22),

      // zline: line,
    });
    // Acumular totales
    t.total_facturado += n(9);
    t.itbis_facturado += n(10);
    t.itbis_retenido += n(11);
    t.itbis_proporcionalidad += n(12);
    t.itbis_costo += n(13);
    t.itbis_adelantar += n(14);
    t.itbis_percibido += n(15);
    t.retencion_renta += n(17);
    t.isr_percibido += n(18);
  }

  return { filename, registros, periodo, rnc, totales: t, datos };
};

const FILAS = [
  ["Total Monto Facturado", "total_facturado"],
  ["ITBIS Facturado", "itbis_facturado"],
  ["ITBIS Retenido", "itbis_retenido"],
  ["ITBIS por Adelantar", "itbis_adelantar"],
  ["ITBIS llevado al Costo", "itbis_costo"],
  ["Monto Retención Renta", "retencion_renta"],
  ["ITBIS Sujeto a Proporcionalidad (Art. 349)", "itbis_proporcionalidad"],
  ["ITBIS percibido en Compras", "itbis_percibido"],
  ["ISR percibido en Compras", "isr_percibido"],
];

export default function Formulario606({ data, onChange, readOnly = false }) {
  const inputRef = useRef(null);
  const tiene = data && data.totales;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parse606(ev.target.result, file.name);
        if (parsed.registros === 0) {
          toast.error("No se encontraron registros válidos en el archivo 606");
          return;
        }
        onChange(parsed);
        toast.success(`Archivo 606 procesado: ${parsed.registros} registros`);
      } catch (err) {
        toast.error("Error al procesar el archivo 606");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <div className="py-2" data-testid="form-606">
      {!readOnly && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <input ref={inputRef} type="file" accept=".txt,text/plain" className="hidden" onChange={handleFile} data-testid="input-606" />
          <Button variant="outline" onClick={() => inputRef.current?.click()} data-testid="upload-606-button">
            <Upload className="w-4 h-4 mr-2" /> Subir archivo 606 (.txt)
          </Button>
          {tiene && (
            <>
              <span className="inline-flex items-center gap-2 text-sm text-slate-600">
                <FileText className="w-4 h-4 text-blue-600" />
                {data.filename} · {data.registros} registros
                {data.periodo ? ` · Período ${data.periodo}` : ""}
              </span>
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onChange({})} data-testid="limpiar-606-button">
                <X className="w-4 h-4 mr-1" /> Limpiar
              </Button>
            </>
          )}
        </div>
      )}

      {!tiene ? (
        <div className="text-center py-12 text-slate-500 border border-dashed rounded-lg" data-testid="form-606-empty">
          <FileText className="w-12 h-12 mx-auto mb-3 text-slate-400" />
          <p>{readOnly ? "No se ha cargado un archivo 606." : "Sube el archivo 606 para ver el resumen del envío."}</p>
        </div>
      ) : (
        <div>
          <h3 className="text-lg font-semibold text-slate-700 mb-3">Resumen del Envío</h3>
          <div className="rounded-lg border border-slate-200 overflow-hidden max-w-2xl" data-testid="resumen-606-table">
            <table className="w-full text-sm">
              <tbody>
                {FILAS.map(([label, key], i) => (
                  <tr key={key} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"} data-testid={`resumen-606-${key}`}>
                    <td className="py-3 px-4 text-slate-700 border-b border-slate-100">{label}</td>
                    <td className="py-3 px-4 text-right font-mono font-medium text-slate-800 border-b border-slate-100 w-48">
                      ${fmtNum(data.totales[key] || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
